import i18n from "@/i18n";
import React, { useEffect, useState } from "react";
import { CalendarCheck2 } from "lucide-react";
import { canManageAbsences } from "@/components/workforce/absenceShared";
import DeficitExplanationForm, { categoryLabel } from "@/components/workforce/DeficitExplanationForm";

// Block B — end-of-cycle Averaging settlement as frozen by this Close plus its
// current validity. Read-only except the audited deficit explanation.
const STATUS_TEXT = {
    get SETTLED() { return i18n.t("wf.AveragingSettlementPanel.STATUS_TEXT.SETTLED", "Cycle settled"); },
    get ALREADY_SETTLED() { return i18n.t("wf.AveragingSettlementPanel.STATUS_TEXT.ALREADY_SETTLED", "Cycle already settled"); },
    get PENDING() { return i18n.t("wf.AveragingSettlementPanel.STATUS_TEXT.PENDING", "Cycle result is provisional — not settled yet"); },
    get UNSUPPORTED() { return i18n.t("wf.AveragingSettlementPanel.STATUS_TEXT.UNSUPPORTED", "Cycle cannot be settled automatically"); },
};
const VALIDITY_TEXT = {
    get INVALIDATED() { return i18n.t("wf.AveragingSettlementPanel.VALIDITY_TEXT.INVALIDATED", "The previous settlement was invalidated because a source period was reopened."); },
    get NOT_CURRENT() { return i18n.t("wf.AveragingSettlementPanel.VALIDITY_TEXT.NOT_CURRENT", "This settlement is no longer current — its inputs changed."); },
};
const h = (m) => `${(Number(m || 0) / 60).toFixed(2)} h`;

const Row = ({ label, value, id }) => (
    <div className="flex justify-between gap-4"><span>{label}</span>
        <span className="font-medium tabular-nums" data-testid={id}>{value}</span></div>
);

const Quantities = ({ q, id }) => (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-xs mt-2" data-testid={`settlement-quantities-${id}`}>
        <Row label={i18n.t("wf.averagingSettlementPanel.target", "Target")} value={h(q.target_minutes)} id={`settlement-target-${id}`} />
        <Row label={i18n.t("wf.averagingSettlementPanel.workedCounted", "Worked (counted)")} value={h(q.worked_countable_minutes)} id={`settlement-worked-${id}`} />
        <Row label={i18n.t("wf.averagingSettlementPanel.creditsVvAbsences", "Credits (VV + absences)")} value={h(q.credited_minutes)} id={`settlement-credits-${id}`} />
        <Row label={i18n.t("wf.averagingSettlementPanel.paidInPeriods130HNot", "Paid in periods (>130 h, not counted again)")} value={h(q.period_level_excess_minutes)}
            id={`settlement-period-excess-${id}`} />
        <Row label={i18n.t("wf.averagingSettlementPanel.surplusAdditional50100", "Surplus: additional / 50 % / 100 %")} id={`settlement-surplus-${id}`}
            value={`${h(q.surplus_additional_minutes)} / ${h(q.surplus_ot50_minutes)} / ${h(q.surplus_ot100_minutes)}`} />
        <Row label={i18n.t("wf.averagingSettlementPanel.deficitNoAutomaticDeduction", "Deficit (no automatic deduction)")} value={h(q.deficit_minutes)} id={`settlement-deficit-${id}`} />
    </div>
);

const SettlementItem = ({ s, user, restaurantId }) => {
    const [history, setHistory] = useState(s.history || []);
    useEffect(() => setHistory(s.history || []), [s]);
    const f = s.frozen;
    const id = f.averaging_cycle_id;
    const cur = s.current_settlement;
    const live = s.assessment || {};
    const status = cur ? "SETTLED" : (live.status === "UNSUPPORTED" ? "UNSUPPORTED" : "PENDING");
    const pending = cur ? [] : (live.pending_confirmations || []);
    // Plain messages replace the technical codes they explain.
    const reasons = cur || pending.length ? [] : (live.reasons || f.reasons || []);
    const q = (cur && cur.quantities) || live.quantities || f.quantities;
    const explanations = history.filter((e) => e.entry_kind === "DEFICIT_EXPLANATION");
    const canExplain = canManageAbsences(user) && cur && cur.quantities.deficit_minutes > 0;
    return (
        <div data-testid={`averaging-settlement-${id}`} className="space-y-1">
            <div className="flex items-start gap-2 font-medium">
                <CalendarCheck2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span data-testid={`averaging-settlement-status-${id}`}>
                    {s.employee_name ? `${s.employee_name}: ` : ""}{STATUS_TEXT[status]}
                    {cur ? ` (version ${cur.settlement_version})` : ""}
                    {` · ${f.cycle_start_date}–${f.cycle_end_date}`}
                </span>
            </div>
            <div className="ml-6 text-xs text-slate-600" data-testid={`averaging-settlement-at-close-${id}`}>
                At this Close: {STATUS_TEXT[f.status] || f.status}
                {f.settlement_version ? ` (version ${f.settlement_version})` : ""}
            </div>
            {VALIDITY_TEXT[s.current_validity] && (
                <div className="ml-6 text-amber-800" data-testid={`averaging-settlement-validity-${id}`}>
                    {VALIDITY_TEXT[s.current_validity]}
                </div>
            )}
            {reasons.length > 0 && (
                <div className="ml-6 text-xs" data-testid={`averaging-settlement-reasons-${id}`}>
                    {reasons.join(" · ")}
                </div>
            )}
            {pending.map((p, i) => (
                <div key={i} className="ml-6 text-amber-800" data-testid={`averaging-settlement-pending-${id}-${i}`}>
                    {p.message}
                </div>
            ))}
            {q && <div className="ml-6"><Quantities q={q} id={id} /></div>}
            {explanations.map((e) => (
                <div key={e.entry_seq} className="ml-6 text-xs" data-testid={`deficit-explanation-${id}-${e.entry_seq}`}>
                    {categoryLabel(e.category)}: {e.text}
                </div>
            ))}
            {canExplain && (
                <div className="ml-6 pt-2">
                    <DeficitExplanationForm cycleId={id} restaurantId={restaurantId}
                        onSaved={(e) => setHistory((h0) => [...h0, e])} />
                </div>
            )}
        </div>
    );
};

export const AveragingSettlementPanel = ({ closeState, user, restaurantId }) => {
    const rows = (closeState && closeState.averaging_validity
        && closeState.averaging_validity.settlements) || [];
    if (!rows.length) return null;
    return (
        <div data-testid="averaging-settlement-panel"
            className="rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-900 space-y-3">
            {rows.map((s) => (
                <SettlementItem key={s.frozen.averaging_cycle_id} s={s} user={user} restaurantId={restaurantId} />
            ))}
        </div>
    );
};

export default AveragingSettlementPanel;
